/**
 * Central page metadata.
 *
 * Title and subtitle for every page in the app live here.
 * AppHeader reads this automatically based on the current route — pages
 * don't need to import or call anything to get their default header.
 *
 * If a page needs dynamic content in the title (e.g. "Welcome back, James")
 * or page-level actions tied to local state, it can override via the
 * `usePageHeader` hook from HeaderContext.
 *
 * Status-aware titles: an entry may define a `byStatus` map. When the URL
 * carries a `status` query param, getPageMetadata overlays the matching
 * variant on top of the base entry (e.g. "/contracts?status=accepted"
 * becomes "Accepted Contracts"). Anything a variant omits falls back to
 * the base entry.
 */

export const PAGE_METADATA = {
  "/dashboard": {
    title: "Dashboard",
    subtitle: "Here's a snapshot of your contracts today",
  },
  "/home": {
    title: "Dashboard",
    subtitle: "Here's a snapshot of your contracts today",
  },
  "/": {
    title: "Dashboard",
    subtitle: "Here's a snapshot of your contracts today",
  },
  "/home": {
    title: "Dashboard",
    subtitle: "Here's a snapshot of your contracts today",
  },
  "/accounts": {
    title: "Accounts",
    subtitle: "Manage your account roster",
  },
  "/contracts": {
    title: "Contracts",
    subtitle: "Search, filter, and export your contracts",
    byStatus: {
      accepted: { title: "Accepted Contracts" },
      pending: { title: "Pending Contracts" },
      delivered: { title: "Delivered Contracts" },
      abandoned: { title: "Abandoned Contracts" },
      cancelled: { title: "Cancelled Contracts" },
    },
  },
  "/document-upload": {
    title: "Document Upload",
    subtitle: "Upload supporting documents for your contracts",
  },
  "/reports": {
    title: "Reports",
    subtitle: "Export reports",
  },
  "/on-demand-report-request": {
    title: "Reports",
    subtitle: "Request custom reports",
  },
  "/library": {
    title: "Library",
    subtitle: "Reference materials, forms, and guides",
  },
  "/customer/profile": {
    title: "Customer Profile",
    subtitle: "Associated customers and providers",
  },
  "/commissions-contracts": {
    title: "Commissions",
    subtitle: "View list of contracts for commission approval",
  },
  "/commissions-payout-runs": {
    title: "Commissions",
    subtitle: "View list of payout runs for commission amount approval",
  },
  "/impersonate": {
    title: "Impersonate",
    subtitle: "Switch into a user's account for support",
  },
  "/enhancement": {
    title: "Enhancement Request",
    subtitle: "Submit ideas to improve the portal",
  },
  "/contract-payment-history": {
    title: "Contract Payment History",
    subtitle: "View Payment History",
  },
  "/contracts/:number/disbursement-quote-request": {
    title: "Disbursement Quote",
    subtitle: "Request a disbursement quote",
  },
};

function patternMatches(pattern, pathname) {
  const patternSegs = pattern.split("/").filter(Boolean);
  const pathSegs = pathname.split("/").filter(Boolean);

  if (patternSegs.length !== pathSegs.length) return false;

  for (let i = 0; i < patternSegs.length; i++) {
    const p = patternSegs[i];
    if (p.startsWith(":")) continue; // wildcard matches anything
    if (p !== pathSegs[i]) return false;
  }

  return true;
}

/**
 * Resolves the closest matching metadata entry for a pathname.
 * Tries the exact path first, then a pattern match, then progressively
 * shorter prefixes — so "/contracts/12345" falls back to "/contracts".
 */
function resolveBase(pathname) {
  // 1. Exact match
  if (PAGE_METADATA[pathname]) return PAGE_METADATA[pathname];

  // 2. Pattern match (most-specific first — more segments wins)
  const patternKeys = Object.keys(PAGE_METADATA)
    .filter((key) => key.includes(":"))
    .sort((a, b) => b.split("/").length - a.split("/").length);

  for (const pattern of patternKeys) {
    if (patternMatches(pattern, pathname)) {
      return PAGE_METADATA[pattern];
    }
  }

  // 3. Prefix fallback (strip segments off the end)
  const segments = pathname.split("/").filter(Boolean);
  for (let i = segments.length; i > 0; i--) {
    const path = "/" + segments.slice(0, i).join("/");
    if (PAGE_METADATA[path]) return PAGE_METADATA[path];
  }

  return { title: null, subtitle: null };
}

/**
 * Resolves page metadata for a pathname, optionally overlaying a
 * status-specific variant when the URL carries a `status` query param.
 *
 * @param {string} pathname - the current location pathname
 * @param {string} [search] - the current location search string (e.g. "?status=accepted")
 */
export function getPageMetadata(pathname, search = "") {
  const base = resolveBase(pathname);

  // Overlay status-specific title/subtitle if this entry defines any.
  if (base.byStatus && search) {
    const status = (
      new URLSearchParams(search).get("status") || ""
    ).toLowerCase();
    const override = base.byStatus[status];
    if (override) {
      return { ...base, ...override };
    }
  }

  return base;
}
